import type { Namespace } from '../types';

// Dedicated Notifications inbox page (R-112). DE drafted — native-review queue.
export const notifications = {
  title: { de: 'Benachrichtigungen', en: 'Notifications' },
  desc: {
    de: 'Ihre Benachrichtigungen — anklicken, um zur betroffenen Stelle zu springen.',
    en: 'Your notifications — click one to jump to what it’s about.',
  },

  filterAll: { de: 'Alle', en: 'All' },
  filterUnread: { de: 'Ungelesen', en: 'Unread' },
  // Below 599px the three tab-segment filters become plain <select> dropdowns
  // (no room for three multi-option tab strips on a phone) — these are their
  // field labels, since a bare <select> carries no visible label of its own.
  ariaFilterStatus: { de: 'Nach Status filtern', en: 'Filter by status' },
  ariaFilterSeverity: { de: 'Nach Schweregrad filtern', en: 'Filter by severity' },
  ariaFilterKind: { de: 'Nach Art filtern', en: 'Filter by kind' },
  // The backend filters by MINIMUM severity (`severities_at_least`) — "Warning"
  // returns warnings AND criticals. The labels have to say so, or an operator
  // reads the criticals in a "Warning" list as a bug.
  sevAll: { de: 'Alle Stufen', en: 'All severities' },
  sevInfo: { de: 'Info und höher', en: 'Info and up' },
  sevWarning: { de: 'Warnung und höher', en: 'Warning and up' },
  sevCritical: { de: 'Nur kritisch', en: 'Critical only' },
  // A row's own severity, spelled out: the coloured dot alone says nothing to a
  // screen reader or to a colour-blind operator.
  severityInfo: { de: 'Info', en: 'Info' },
  severityWarning: { de: 'Warnung', en: 'Warning' },
  severityCritical: { de: 'Kritisch', en: 'Critical' },

  // R-353 — the crop-cycle filter. A kind PREFIX (`cultivation.`), the same one
  // the backend's notification rules fan out on, so "crop only" here means
  // exactly what "crop" means there.
  kindAll: { de: 'Alle Arten', en: 'All kinds' },
  kindCropOnly: { de: 'Nur Kulturen', en: 'Crop only' },

  // The listing's module chip — `kind`'s dotted prefix (`cultivation.harvest_due`
  // → "CULTIVATION"), read off every module that has actually raised a
  // notification in production (grepped from every `Notification::new(...)`
  // call site, not guessed). `asset.` is the backend's own legacy-singular
  // quirk for the same domain as `assets.` (see `notifications_view.rs`) and
  // deliberately shares this label — the quirk stays real for filtering, but
  // an operator reading a chip doesn't need two labels for one domain. An
  // unrecognised module (a future kind this list hasn't caught up to) falls
  // back to its own raw text rather than disappearing — see `moduleOf` in
  // `notifications.tsx`.
  moduleCultivation: { de: 'Kultivierung', en: 'Cultivation' },
  moduleAssets: { de: 'Anlagen', en: 'Assets' },
  moduleClimate: { de: 'Klima', en: 'Climate' },
  moduleEcommerce: { de: 'Bestellungen', en: 'Orders' },
  moduleItsm: { de: 'ITSM', en: 'ITSM' },
  moduleLogistics: { de: 'Logistik', en: 'Logistics' },
  moduleMaintenance: { de: 'Wartung', en: 'Maintenance' },
  moduleProcurement: { de: 'Beschaffung', en: 'Procurement' },
  moduleSecurity: { de: 'Sicherheit', en: 'Security' },
  moduleService: { de: 'Service', en: 'Service' },
  moduleSite: { de: 'Standort', en: 'Site' },
  moduleWarehouse: { de: 'Lager', en: 'Warehouse' },
  moduleAlerting: { de: 'Alarmierung', en: 'Alerting' },

  // The single column head above each recency panel's row list, ported from
  // Fleet Overview's Active Alarms table ("ALARM" there → "NOTIFICATION" here).
  colNotification: { de: 'Benachrichtigung', en: 'Notification' },
  colAction: { de: 'Aktion', en: 'Action' },

  markRead: { de: 'Als gelesen', en: 'Mark read' },
  acknowledge: { de: 'Bestätigen', en: 'Acknowledge' },
  acked: { de: 'Bestätigt', en: 'Acknowledged' },
  markAllRead: { de: 'Alle als gelesen', en: 'Mark all read' },

  // A filtered-empty inbox and a genuinely empty one are different situations —
  // one has a next step (clear the filter), the other means "nothing happened".
  emptyTitle: { de: 'Keine Benachrichtigungen', en: 'No notifications' },
  emptyMsg: {
    de: 'Sie haben noch keine Benachrichtigungen erhalten.',
    en: "You haven't received any notifications yet.",
  },
  emptyFilteredTitle: { de: 'Nichts passt zum Filter', en: 'Nothing matches this filter' },
  emptyFilteredMsg: {
    de: 'Filter zurücksetzen, um alle Benachrichtigungen zu sehen.',
    en: 'Clear the filters to see every notification.',
  },
  clearFilters: { de: 'Filter zurücksetzen', en: 'Clear filters' },
  loadError: { de: 'Benachrichtigungen konnten nicht geladen werden', en: "Couldn't load notifications" },

  // Recency grouping: an inbox read top-down needs to know where "now" ends.
  groupToday: { de: 'Heute', en: 'Today' },
  groupYesterday: { de: 'Gestern', en: 'Yesterday' },
  groupEarlier: { de: 'Früher', en: 'Earlier' },
  otherSite: { de: 'Anderer Standort', en: 'Other site' },
} as const satisfies Namespace;
